import { createContext, useContext, useEffect, useState } from "react";
import {
  clearAuthToken,
  getMe,
  loginUser,
  registerUser,
  setAuthToken,
} from "@/api";

const AuthContext = createContext(null);

const extractErrorMessage = (error) =>
  error?.response?.data?.detail || "The archives are sealed. Try again.";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState("");

  const hydrateUser = async () => {
    const token = localStorage.getItem("dracon_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await getMe();
      setUser(data);
    } catch (error) {
      clearAuthToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    hydrateUser();
  }, []);

  const login = async (payload) => {
    setLoading(true);
    setStatusMessage("");
    try {
      const { data } = await loginUser(payload);
      setAuthToken(data.access_token);
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      const message = extractErrorMessage(error);
      setStatusMessage(message);
      clearAuthToken();
      setUser(null);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload) => {
    setLoading(true);
    setStatusMessage("");
    try {
      const { data } = await registerUser(payload);
      setAuthToken(data.access_token);
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      const message = extractErrorMessage(error);
      setStatusMessage(message);
      clearAuthToken();
      setUser(null);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAuthToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        statusMessage,
        setStatusMessage,
        login,
        register,
        logout,
        refreshUser: hydrateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
